import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axiosInstance from "../../../../Config/axiosInstance";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import "./ProjectCodeDialog.css";

const animatedComponents = makeAnimated();
const PREVIEW_URL = "/subprojectcodes/preview/";

export default function SubprojectCodeEdit({
  open,
  onClose,
  subprojectData,
  projectId,
  onSave,
}) {
  const [departments, setDepartments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [projectDetails, setProjectDetails] = useState(null);

  const [loadingLists, setLoadingLists] = useState(false);
  const [department, setDepartment] = useState(null);
  const [activity, setActivity] = useState(null);
  const [opportunity, setOpportunity] = useState(null);
  const [description, setDescription] = useState("");

  const [organizationName, setOrganizationName] = useState("");
  const [clientName, setClientName] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("active");

  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const previewReqIdRef = useRef(0);

  const [errors, setErrors] = useState({
    department: "",
    activity: "",
    description: "",
    opportunity: "",
    code: "",
    submit: "",
  });

  const setSubmitError = (e) => {
    const errorResponse = e?.response?.data;
    let msg = "Unknown error";

    if (typeof errorResponse === "string") msg = errorResponse;
    else if (errorResponse) msg = JSON.stringify(errorResponse);
    else msg = e?.message || "Unknown error";

    setErrors((prev) => ({ ...prev, submit: msg }));
  };

  const projectCodeLabel = useMemo(() => {
    if (projectDetails?.code) return projectDetails.code;
    if (projectDetails?.project_code) return projectDetails.project_code;
    if (subprojectData?.project_code) return subprojectData.project_code;
    return `#${projectId}`;
  }, [projectDetails, subprojectData, projectId]);

  const statusLabel = useMemo(() => {
    const normalized = (status || "").toString().trim().toLowerCase();
    return normalized === "completed" ? "Completed" : "Active";
  }, [status]);

  const fetchProjectDetails = useCallback(async () => {
    if (!projectId) return null;

    try {
      const response = await axiosInstance.get(`/code_registries/${projectId}/`);
      if (response.data) {
        setProjectDetails(response.data);
        return response.data;
      }
    } catch (error) {
      console.error(
        "Eroare la fetchProjectDetails:",
        error?.response?.data || error?.message || error
      );
    }

    return null;
  }, [projectId]);

  const departmentOptions = useMemo(
    () =>
      (departments || []).map((d) => ({
        value: d.id,
        label: `${d.name}${
          d.department_abbreviated ? ` (${d.department_abbreviated})` : ""
        }`,
        __raw: d,
      })),
    [departments]
  );

  const activityOptions = useMemo(() => {
    const depId = department?.value;

    const filtered = depId
      ? (activities || []).filter((m) => String(m.department) === String(depId))
      : activities || [];

    return filtered.map((m) => ({
      value: m.id,
      label: `${m.activity}${
        m.activity_abbreviated ? ` (${m.activity_abbreviated})` : ""
      }`,
      __raw: m,
    }));
  }, [activities, department]);

  const opportunityOptions = useMemo(
    () =>
      (opportunities || []).map((o) => ({
        value: o.id,
        label: `${o.code_opportunity ?? ""} — ${
          o.project_name ??
          o.title_opportunity ??
          o.title ??
          o.name ??
          `Opportunity #${o.id}`
        }`,
        department_id:
          o.department_id ??
          o.department?.id ??
          o.department ??
          o.department_details?.id ??
          null,
        __raw: o,
      })),
    [opportunities]
  );

  const fetchPreview = useCallback(
    async ({ act, opp }) => {
      if (!open || !projectId || !act?.value) return;

      const reqId = ++previewReqIdRef.current;
      setPreviewLoading(true);

      try {
        const payload = {
          project_code: projectId,
          matrix: Number(act.value),
          opportunity_id: opp?.value ?? null,
        };

        const res = await axiosInstance.post(PREVIEW_URL, payload);

        if (reqId !== previewReqIdRef.current) return;

        const newCode =
          res?.data?.code ??
          res?.data?.preview ??
          res?.data?.generated_code ??
          (typeof res?.data === "string" ? res.data : "");

        if (newCode) {
          setCode(newCode);
          setErrors((prev) => ({ ...prev, code: "" }));
        }
      } catch (e) {
        if (reqId !== previewReqIdRef.current) return;
        console.error("Preview error:", e?.response?.data || e?.message || e);
      } finally {
        if (reqId === previewReqIdRef.current) setPreviewLoading(false);
      }
    },
    [open, projectId]
  );

  useEffect(() => {
    if (!open) return;

    setDepartment(null);
    setActivity(null);
    setOpportunity(null);
    setDescription("");
    setStatus("active");
    setSubmitted(false);
    setProjectDetails(null);
    setPreviewLoading(false);
    setOpportunities([]);
    setOrganizationName("");
    setClientName("");
    setCode("");

    setErrors({
      department: "",
      activity: "",
      description: "",
      opportunity: "",
      code: "",
      submit: "",
    });

    const loadListsAndPrefill = async () => {
      setLoadingLists(true);

      try {
        const projectData = await fetchProjectDetails();

        const [depRes, actRes, cliRes, oppRes] = await Promise.all([
          axiosInstance.get("/departments/"),
          axiosInstance.get("/code_matrix/"),
          axiosInstance.get("/clients/"),
          axiosInstance.get("/opportunities/"),
        ]);

        const deps = depRes?.data || [];
        const acts = actRes?.data || [];
        const clients = cliRes?.data || [];
        const allOpps = oppRes?.data || [];

        setDepartments(deps);
        setActivities(acts);

        const oppsFree = allOpps.filter((o) => {
          const oppPcId = o.project_code_id ?? o.project_code;

          const spId =
            o.subproject_code_id ??
            o.subproject_code?.id ??
            o.subproject_code ??
            null;

          const isSameParent = Number(oppPcId) === Number(projectId);

          const isFree =
            spId === null ||
            spId === "" ||
            Number(spId) === 0 ||
            Number(spId) === Number(subprojectData?.id);

          return isSameParent && isFree;
        });

        setOpportunities(oppsFree);

        const clientMap = clients.reduce((acc, c) => {
          acc[c.id] = c.name ?? c.denumire ?? String(c.id);
          return acc;
        }, {});

        if (!subprojectData) return;

        setDescription(subprojectData.description || "");

        setStatus(
          (subprojectData.status || "active").toString().trim().toLowerCase() ===
            "completed"
            ? "completed"
            : "active"
        );

        setCode(subprojectData.code || "");

        let organizationLabel = "";

        if (projectData?.organization_name) {
          organizationLabel = projectData.organization_name;
        } else if (projectData?.organization_label) {
          organizationLabel = projectData.organization_label;
        } else if (projectData?.organization?.name) {
          organizationLabel = projectData.organization.name;
        } else if (projectData?.organization_details?.name) {
          organizationLabel = projectData.organization_details.name;
        }

        setOrganizationName(String(organizationLabel));

        let clientLabel = "";

        if (projectData?.client_name) {
          clientLabel = projectData.client_name;
        } else if (projectData?.client_label) {
          clientLabel = projectData.client_label;
        } else if (projectData?.client_details?.name) {
          clientLabel = projectData.client_details.name;
        } else if (projectData?.client?.name) {
          clientLabel = projectData.client.name;
        } else if (projectData?.client) {
          clientLabel = clientMap[projectData.client] ?? String(projectData.client);
        } else {
          clientLabel = subprojectData.client ? String(subprojectData.client) : "";
        }

        setClientName(String(clientLabel));

        const selectedOppId = subprojectData.opportunity_id_selected ?? null;

        const selectedOppObj =
          selectedOppId != null
            ? allOpps.find((o) => String(o.id) === String(selectedOppId)) || null
            : null;

        let mergedOpps = [...oppsFree];

        if (
          selectedOppObj &&
          !mergedOpps.some((o) => String(o.id) === String(selectedOppObj.id))
        ) {
          mergedOpps = [selectedOppObj, ...mergedOpps];
          setOpportunities(mergedOpps);
        }

        const oppSel = selectedOppObj
          ? {
              value: selectedOppObj.id,
              label: `${selectedOppObj.code_opportunity ?? ""} — ${
                selectedOppObj.project_name ??
                selectedOppObj.title_opportunity ??
                selectedOppObj.title ??
                selectedOppObj.name ??
                `Opportunity #${selectedOppObj.id}`
              }`,
              department_id:
                selectedOppObj.department_id ??
                selectedOppObj.department?.id ??
                selectedOppObj.department ??
                selectedOppObj.department_details?.id ??
                null,
              __raw: selectedOppObj,
            }
          : null;

        setOpportunity(oppSel);

        const matrixId = subprojectData.matrix;

        const matrixObj =
          matrixId != null
            ? acts.find((m) => String(m.id) === String(matrixId)) || null
            : null;

        const depIdFromOpportunity = oppSel?.department_id ?? null;

        const depObjFromOpportunity =
          depIdFromOpportunity != null
            ? deps.find((d) => String(d.id) === String(depIdFromOpportunity)) ||
              null
            : null;

        const depObjFromMatrix = matrixObj?.department
          ? deps.find((d) => String(d.id) === String(matrixObj.department)) || null
          : null;

        const depObj = depObjFromOpportunity || depObjFromMatrix;

        const depSel = depObj
          ? {
              value: depObj.id,
              label: `${depObj.name}${
                depObj.department_abbreviated
                  ? ` (${depObj.department_abbreviated})`
                  : ""
              }`,
              __raw: depObj,
            }
          : null;

        const actSel = matrixObj
          ? {
              value: matrixObj.id,
              label: `${matrixObj.activity}${
                matrixObj.activity_abbreviated
                  ? ` (${matrixObj.activity_abbreviated})`
                  : ""
              }`,
              __raw: matrixObj,
            }
          : null;

        setDepartment(depSel);
        setActivity(actSel);
      } catch (e) {
        console.error("Error loading data:", e);
        setSubmitError(e);
      } finally {
        setLoadingLists(false);
      }
    };

    loadListsAndPrefill();
  }, [open, subprojectData, fetchProjectDetails, projectId]);

  const validateAll = useCallback(() => {
    const next = {
      department: "",
      activity: "",
      description: "",
      opportunity: "",
      code: "",
      submit: "",
    };

    if (!subprojectData?.id) next.submit = "Subproject data is missing.";
    if (!opportunity) next.opportunity = "Required";
    if (!department) next.department = "Required";
    if (!activity) next.activity = "Required";
    if (!description?.trim()) next.description = "Required";
    if (!code?.trim()) next.code = "Required";

    setErrors((prev) => ({ ...prev, ...next }));

    return (
      !next.submit &&
      !next.opportunity &&
      !next.department &&
      !next.activity &&
      !next.description &&
      !next.code
    );
  }, [subprojectData, opportunity, department, activity, description, code]);

  const handleSubmit = async () => {
    setSubmitted(true);
    setErrors((prev) => ({ ...prev, submit: "" }));

    if (!validateAll()) return;

    setSaving(true);

    try {
      const initial = {
        matrix: subprojectData?.matrix ?? null,
        opportunity_id: subprojectData?.opportunity_id_selected ?? null,
        description: (subprojectData?.description ?? "").trim(),
        status:
          (subprojectData?.status ?? "active").toString().trim().toLowerCase() ===
          "completed"
            ? "completed"
            : "active",
        code: (subprojectData?.code ?? "").trim(),
      };

      const current = {
        matrix: activity?.value != null ? Number(activity.value) : null,
        opportunity_id: opportunity?.value ?? null,
        description: (description ?? "").trim(),
        status,
        code: (code ?? "").trim(),
      };

      const updateData = {};

      if (current.matrix !== initial.matrix) {
        updateData.matrix = current.matrix;
      }

      if ((current.opportunity_id ?? null) !== (initial.opportunity_id ?? null)) {
        updateData.opportunity_id = current.opportunity_id ?? null;
      }

      if (current.description !== initial.description) {
        updateData.description = current.description;
      }

      if (current.status !== initial.status) {
        updateData.status = current.status;
      }

      if (current.code && current.code !== initial.code) {
        updateData.code = current.code;
      }

      if (Object.keys(updateData).length === 0) {
        onClose(true);
        return;
      }

      const response = await axiosInstance.patch(
        `/subprojectcodes/${subprojectData.id}/`,
        updateData
      );

      if (onSave) onSave(response.data);
      onClose(true);
    } catch (e) {
      console.error("Save error:", e);

      let errorMsg = "Failed to save changes";

      if (e.response?.data) {
        const data = e.response.data;

        if (typeof data === "string") errorMsg = data;
        else if (data.detail) errorMsg = data.detail;
        else if (data.message) errorMsg = data.message;
        else if (typeof data === "object") {
          const fieldErrors = Object.entries(data)
            .map(([field, msg]) => `${field}: ${msg}`)
            .join(", ");
          errorMsg = fieldErrors || JSON.stringify(data);
        }
      } else if (e.request) {
        errorMsg = "No response from server. Please check your connection.";
      } else {
        errorMsg = e.message || "Unknown error occurred";
      }

      setErrors((prev) => ({ ...prev, submit: errorMsg }));
    } finally {
      setSaving(false);
    }
  };

  const disabledAll = saving || loadingLists;

  const showDeptError = submitted && !!errors.department;
  const showActError = submitted && !!errors.activity;
  const showDescError = submitted && !!errors.description;
  const showOppError = submitted && !!errors.opportunity;
  const showCodeError = submitted && !!errors.code;

  return (
    <Dialog
      open={open}
      onClose={disabledAll ? undefined : () => onClose(false)}
      maxWidth="sm"
      fullWidth
      className="project-code-dialog"
    >
      <DialogTitle className="spc-title">
        <div className="spc-header">
          <div className="spc-header-top">
            <span className="spc-header-title">Edit Sub Project Code</span>
          </div>

          <div className="spc-header-sub">
            <span className="spc-header-sub-label">Project</span>
            <span className="spc-header-sub-sep">:</span>
            <span className="spc-header-sub-value">{projectCodeLabel}</span>
          </div>

          <div className="spc-header-sub" style={{ marginTop: "4px" }}>
            <span className="spc-header-sub-label">Subproject Code</span>
            <span className="spc-header-sub-sep">:</span>
            <span className="spc-header-sub-value">
              {subprojectData?.code || ""}
            </span>
          </div>
        </div>
      </DialogTitle>

      <DialogContent dividers className="cmd-content">
        {loadingLists ? (
          <div
            style={{ display: "flex", justifyContent: "center", padding: "30px" }}
          >
            <CircularProgress size={40} />
          </div>
        ) : (
          <>
            {/* Organization */}
            <div className="cmd-form-group">
              <label className="cmd-label">Organization</label>

              <input
                type="text"
                className="cmd-input"
                value={organizationName}
                readOnly
                disabled
                placeholder="Auto from project code"
              />
            </div>

            {/* Client */}
            <div className="cmd-form-group">
              <label className="cmd-label">Client</label>

              <input
                type="text"
                className="cmd-input"
                value={clientName}
                readOnly
                disabled
                placeholder="Auto from project code"
              />
            </div>

            <div className="cmd-form-group">
              <label className="cmd-label">
                Opportunity <span style={{ color: "red" }}>*</span>
              </label>

              <Select
                options={opportunityOptions}
                value={opportunity}
                onChange={(val) => {
                  setOpportunity(val);

                  if (!val) {
                    setDepartment(null);
                    setActivity(null);
                    setCode("");
                    return;
                  }

                  setErrors((prev) => ({ ...prev, opportunity: "" }));

                  const depId =
                    val.department_id ??
                    val.__raw?.department_id ??
                    val.__raw?.department?.id ??
                    val.__raw?.department ??
                    val.__raw?.department_details?.id ??
                    null;

                  const depOption =
                    depId != null
                      ? departmentOptions.find(
                          (d) => String(d.value) === String(depId)
                        ) || null
                      : null;

                  setDepartment(depOption);
                  setActivity(null);
                  setCode("");

                  if (depOption) {
                    setErrors((prev) => ({ ...prev, department: "" }));
                  }
                }}
                isClearable
                isSearchable
                placeholder="Select opportunity"
                classNamePrefix="cmd-react-select"
                components={animatedComponents}
                isDisabled={disabledAll}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 20000 }),
                  menu: (base) => ({ ...base, zIndex: 20000 }),
                }}
                className={showOppError ? "cmd-select-error" : ""}
              />

              {showOppError && (
                <div className="cmd-field-error">{errors.opportunity}</div>
              )}
            </div>

            <div className="cmd-form-group">
              <label className="cmd-label">
                Department <span style={{ color: "red" }}>*</span>
              </label>

              <Select
                options={departmentOptions}
                value={department}
                onChange={() => {}}
                isClearable={false}
                isSearchable={false}
                placeholder="Auto from opportunity"
                classNamePrefix="cmd-react-select"
                components={{
                  DropdownIndicator: null,
                  IndicatorSeparator: null,
                }}
                isDisabled
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 20000 }),
                  menu: (base) => ({ ...base, zIndex: 20000 }),
                }}
                className={showDeptError ? "cmd-select-error" : ""}
              />

              {showDeptError && (
                <div className="cmd-field-error">{errors.department}</div>
              )}
            </div>

            <div className="cmd-form-group">
              <label className="cmd-label">
                Activity <span style={{ color: "red" }}>*</span>
              </label>

              <Select
                options={activityOptions}
                value={activity}
                onChange={(val) => {
                  setActivity(val);

                  if (val) {
                    setErrors((prev) => ({ ...prev, activity: "" }));
                  }

                  fetchPreview({ act: val, opp: opportunity });
                }}
                onBlur={() => {
                  if (!submitted) return;

                  setErrors((prev) => ({
                    ...prev,
                    activity: activity ? "" : "Activity is required",
                  }));
                }}
                isClearable
                isSearchable
                placeholder={
                  department ? "Select activity" : "First select a department"
                }
                classNamePrefix="cmd-react-select"
                components={animatedComponents}
                isDisabled={disabledAll || !department}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 20000 }),
                  menu: (base) => ({ ...base, zIndex: 20000 }),
                }}
                className={showActError ? "cmd-select-error" : ""}
              />

              {showActError && (
                <div className="cmd-field-error">{errors.activity}</div>
              )}
            </div>

            <div className="cmd-form-group">
              <label className="cmd-label">
                Code <span style={{ color: "red" }}>*</span>{" "}
                {previewLoading ? (
                  <span style={{ fontSize: 12, opacity: 0.7 }}>
                    (generating...)
                  </span>
                ) : null}
              </label>

              <input
                type="text"
                className={`cmd-input ${showCodeError ? "cmd-input-error" : ""}`}
                value={code}
                readOnly
                disabled
                placeholder="Generated automatically"
              />

              {showCodeError && (
                <div className="cmd-field-error">{errors.code}</div>
              )}
            </div>

            <div className="cmd-form-group">
              <label className="cmd-label">
                Description <span style={{ color: "red" }}>*</span>
              </label>

              <textarea
                rows={6}
                className={`cmd-input ${showDescError ? "cmd-input-error" : ""}`}
                placeholder="Enter description..."
                value={description}
                onChange={(e) => {
                  const val = e.target.value;
                  setDescription(val);

                  if (val?.trim()) {
                    setErrors((prev) => ({ ...prev, description: "" }));
                  }
                }}
                onBlur={() => {
                  if (!submitted) return;

                  setErrors((prev) => ({
                    ...prev,
                    description: description?.trim()
                      ? ""
                      : "Description is required",
                  }));
                }}
                disabled={disabledAll}
                style={{ resize: "vertical" }}
              />

              {showDescError && (
                <div className="cmd-field-error">{errors.description}</div>
              )}
            </div>

            <div className="cmd-form-group">
              <label className="cmd-label">Status</label>

              <input
                type="text"
                className="cmd-input"
                value={statusLabel}
                readOnly
              />
            </div>

            {errors.submit && (
              <div
                className="cmd-submit-error"
                style={{
                  color: "#d32f2f",
                  marginTop: "10px",
                  padding: "10px",
                  backgroundColor: "#ffebee",
                  borderRadius: "4px",
                  whiteSpace: "pre-wrap",
                  fontSize: "0.9rem",
                }}
              >
                <strong>Eroare:</strong> {errors.submit}
              </div>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions className="cmd-actions">
        <Button
          onClick={() => onClose(false)}
          disabled={disabledAll}
          className="cmd-btn cmd-btn-cancel"
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={disabledAll}
          variant="contained"
          className="cmd-btn cmd-btn-create"
          startIcon={
            saving ? <CircularProgress size={18} className="cmd-spinner" /> : null
          }
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}